import React, { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchPlans } from "../services/plansService";
import type { BrandPlan } from "../types/brand.types";

export type PlansStatus = "loading" | "ready" | "failed";

export interface PlansState {
  /** Every plan the workspace has, in the order the API returned them. */
  plans: BrandPlan[];
  status: PlansStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
  /**
   * Writes a saved row straight into the list rather than refetching for it: a
   * create and an edit both answer with the whole record, so the list already
   * has everything a second request would tell it.
   */
  setPlans: React.Dispatch<React.SetStateAction<BrandPlan[]>>;
}

const EMPTY: BrandPlan[] = [];

export const PLANS_QUERY_KEY = ["brand", "plans"] as const;

/**
 * The workspace's plans, loaded when the Plans tab opens.
 *
 * Backed by TanStack Query: a screen visited again shows the last list
 * immediately while a fresh read runs behind it, so a plan edited on
 * another device still catches up within moments instead of making every
 * visit wait on the network.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function usePlans(): PlansState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PLANS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPlans(getToken, signal),
  });

  // Only fires when there is nothing cached to show in its place — see
  // `useDashboard` for why that is what `isError` means here.
  useEffect(() => {
    if (!query.isError) return;
    console.error("plans failed to load:", query.error);
    toast.error(
      "Could not load your plans",
      query.error instanceof ApiError
        ? query.error.message
        : "We could not reach the server. Check your connection and try again."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  const setPlans = useCallback<React.Dispatch<React.SetStateAction<BrandPlan[]>>>(
    (updater) => {
      queryClient.setQueryData<BrandPlan[]>(
        PLANS_QUERY_KEY,
        (current: BrandPlan[] | undefined) =>
          typeof updater === "function"
            ? (updater as (current: BrandPlan[]) => BrandPlan[])(
                current ?? EMPTY
              )
            : updater
      );
    },
    [queryClient]
  );

  return {
    plans: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    reload: () => {
      void query.refetch();
    },
    setPlans,
  };
}

export default usePlans;
